<template>
  <div class="form-identification">
    <h3 class="form-identification__caption">Удостоверение личности</h3>
    <the-select
      class="form-identification__representative__select"
      title="Тип документа"
      placeholder="Найти"
      v-bind:clearable="true"
      v-bind:options="$options.documentTypes"
      v-model="identification.type"
      option-value-key="value"
      option-title-key="name"
      v-bind:violations="identificationTypeError"
    />
    <form-passport
      v-if="isSelectPassport"
      :input-errors="inputErrors"
      :init-identification="initIdentification"
      v-on:on-model-update="onUpdateIdentification"
    >
      <template v-slot:bottom>
        <slot name="unit-code" />
      </template>
    </form-passport>
    <form-foreign-passport
      v-if="isSelectForeignPassport"
      :input-errors="inputErrors"
      :init-identification="initIdentification"
      v-on:on-model-update="onUpdateIdentification"
    />
    <form-birth-certificate
      v-if="isSelectBirthCertificate"
      :input-errors="inputErrors"
      :init-identification="initIdentification"
      v-on:on-model-update="onUpdateIdentification"
    />
    <form-identification-card
      v-if="isSelectIdentificationCard"
      :input-errors="inputErrors"
      :init-identification="initIdentification"
      v-on:on-model-update="onUpdateIdentification"
    />
    <input-iin
      v-bind:violations="iinErrors"
      v-bind:value="initIin"
      v-on:input="onUpdateIin"
      v-if="isShowIin"
    />
    <input-inn
      class="patient-basic__representative__block"
      v-model="identification.inn"
      v-bind:optional="true"
      v-bind:violations="innInputErrors"
      v-if="isShowInn"
    />
    <input-text
      v-if="!isSelectIdentificationCard"
      class="form-identification__belay"
      maxlength="225"
      placeholder="Адрес прописки"
      title="Адрес прописки"
      type="text"
      v-bind:violations="addressInputError"
      v-model="identification.address" />
    <div
      v-if="!isSelectIdentificationCard"
      class="form-identification__copy"
    >
      <button
        type="button"
        title="Адрес проживания совпадает с адресом регистрации"
        v-bind:disabled="!identification.address"
        v-on:click="copyAddressToLivingAddress"
        class="form-identification__copy__button"
      >
        Адрес проживания
      </button>
      <input-text
        class="form-identification__belay"
        maxlength="455"
        placeholder="Адрес проживания"
        type="text"
        v-bind:violations="livingAddressInputError"
        v-model="identification.livingAddress" />
    </div>
  </div>
</template>

<script>
import TheSelect from '@/components/TheSelect.vue';
import { Identification } from '@/storage/gateways/PatientGateway';
import FormForeignPassport from '@/components/FormForeignPassport.vue';
import FormBirthCertificate from '@/components/FormBirthCertificate.vue';
import InputText from '@/components/InputText.vue';
import InputIin from '@/components/InputIin.vue';
import FormIdentificationCard from '@/components/FormIdentificationCard.vue';
import FormPassport from '@/components/FormPassport.vue';
import InputInn from '@/components/InputInn.vue';
/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

/**
 * @description Получить уникальные значения.
 * @param {any[]} values
 * @returns {any[]}
 */
const unique = (values) => {
  if (values.length < 2) {
    return values;
  }
  return Array.from(new Set(values));
};

/**
 * События обновления модели
 * @type {string}
 */
const ON_MODEL_UPDATE = 'on-model-update';
/**
 * События обновления ИИН.
 * @type {string}
 */
const ON_UPDATE_IIN = 'on-update-iin';

/**
 * @description Форма удостоверения личности по сертификату о рождении
 * @type {string}
 */
const BIRTH_FORM = 'birth_certificate';
/**
 * @description Форма удостоверения личности по паспорту
 * @type {string}
 */
const PASSPORT_FORM = 'passport';
/**
 * @description Форма удостоверения личности.
 * @type {string}
 */
const IDENTIFICATION_CARD = 'identification-card';
/**
 * @description Форма удостоверения личности по иностранному паспорту
 * @type {string}
 */
const FOREIGN_PASSPORT_FORM = 'foreign-passport';

/**
 * @description Форма удостоверения личности по виду на жительство
 * @type {string}
 */
const RESIDENCE_PERMIT_FORM = 'residence-permit';

/**
 * @description допустимые типы документов
 * @type {[{name: string, id: number}]}
 */
const documentTypes = [
  {
    id: 0, /* заглушка */
    name: 'Удостоверение личности',
    value: 'identification-card',
  },
  {
    id: 10,
    name: 'Иностранный паспорт',
    value: 'foreign-passport',
  },
  {
    id: 3,
    name: 'Свидетельство о рождении',
    value: 'birth_certificate',
  },
  {
    id: 26,
    name: 'Вид на жительство иностранного гражданина',
    value: 'residence-permit',
  },
];
export default {
  name: 'FormIdentification',
  components: {
    InputInn,
    FormPassport,
    FormIdentificationCard,
    InputIin,
    InputText,
    FormBirthCertificate,
    FormForeignPassport,
    TheSelect,
  },
  computed: {
    identificationTypeError: resolveInputErrors('identification.type'),
    addressInputError: resolveInputErrors('identification.address'),
    innInputErrors: (vm) => unique([
      ...resolveInputErrors('guardian.inn')(vm),
      ...resolveInputErrors('identification.inn')(vm),
    ]),
    livingAddressInputError: resolveInputErrors('identification.livingAddress'),
    /**
     * @description Выбран сертификат о рождении
     * @returns {boolean}
     */
    isSelectBirthCertificate() {
      return this.initIdentification.type === BIRTH_FORM;
    },
    /**
     * @description Выбран паспорт
     * @returns {boolean}
     */
    isSelectPassport() {
      return this.initIdentification.type === PASSPORT_FORM;
    },
    /**
     * @description Выбран сертификат о рождении
     * @returns {boolean}
     */
    isSelectIdentificationCard() {
      return this.initIdentification.type === IDENTIFICATION_CARD;
    },
    /**
     * @description Выбран иностранный паспорт
     * @returns {boolean}
     */
    isSelectForeignPassport() {
      return this.initIdentification.type === FOREIGN_PASSPORT_FORM
        || this.initIdentification.type === RESIDENCE_PERMIT_FORM;
    },
  },
  props: {
    /**
     * @description Инициализационные данные удостоверение личности родителя
     * @type {Identification}
     */
    initIdentification: {
      type: Identification,
      required: true,
    },
    /**
     * @description Нарушение ограничений.
     * @type {Object}
     */
    inputErrors: {
      type: Object,
      required: true,
    },
    /**
     * Показывать поле ИНН
     * @type Boolean
     */
    isShowInn: {
      type: Boolean,
      required: true,
    },
    /**
     * Показывать поле ИИН.
     * @type Boolean
     */
    isShowIin: {
      type: Boolean,
      default: () => true,
    },
    /**
     * @description ИИН
     * @type {string}
     */
    initIin: {
      type: String,
      required: true,
    },
    /**
     * @description Ошибки ввода ИИН
     * @type {Array}
     */
    iinErrors: {
      type: Array,
      default: () => [],
    },
  },
  created() {
    this.identification = this.initIdentification;
  },
  data() {
    return {
      /**
       * @description Удостоверение личности родителя
       * @type {Identification}
       */
      identification: this.initIdentification,
    };
  },
  watch: {
    /**
     * @description Данные родителя обновились
     *
     * @param newValue
     */
    initIdentification: {
      handler(newValue) {
        this.identification = newValue;
      },
      deep: true,
    },
  },
  methods: {
    /**
     * @description Обновление удостоверения личности
     * @param newIdentification {Identification}
     */
    onUpdateIdentification(newIdentification) {
      this.$emit(ON_MODEL_UPDATE, newIdentification);
    },
    /**
     * @description Обновление ИИН.
     * @param newValue {string}
     */
    onUpdateIin(newValue) {
      this.$emit(ON_UPDATE_IIN, newValue);
    },
    /**
     * @description Скопировать адрес прописки в адрес проживания
     * @returns {void}
     */
    copyAddressToLivingAddress() {
      this.identification.livingAddress = this.identification.address;
    },
  },
  documentTypes,
};
</script>

<style scoped lang="scss">

.form-identification {
  display: flex;
  flex-direction: column;
  width: 100%;
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr;
  &__caption {
    margin-bottom: 5px;
    font-weight: 600;
    font-size: 16px;
    line-height: 19px;
    color: #192a3e;
  }
  &__representative {
    flex-direction: column;
    display: flex;
    gap: 10px;
    &__select {
      position: relative;
    }
  }
  &__belay {
    width: 100%;
    &.indent {
      margin: 10px 0 -25px 0;
      width: 100%;
    }
  }
  &__copy {
    &__button {
      display: flex;
      align-items: center;
      font-weight: 400;
      font-size: 12px;
      margin: 0 0 2px 0;
      color: var(--unfocus-color);
      &:after {
        content: '';
        background-image: url("@/assets/img/copy-blue.svg");
        background-repeat: no-repeat;
        background-position: bottom;
        background-size: contain;
        margin: 0 0 0 10px;
        width: 15px;
        height: 15px;
      }
    }
  }
}

</style>
